import { useEffect, useRef, useState } from 'react';

import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, FormControlLabel, Typography
} from '@mui/material';

import { trackEvent } from '../lib/mixpanel';
import { POLICY_CONTENT, POLICY_TITLE } from '../lib/policy';
import { caloTheme } from '../theme/calo';
import Icon from './Icon';

interface PolicyGateProps {
  affiliateId: string;
  onAccept: () => void;
}

const SCROLL_END_THRESHOLD_PX = 24;

const PolicyGate = ({ affiliateId, onAccept }: PolicyGateProps) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // If the content is short enough to not need scrolling, there's nothing to "reach the end" of.
    const el = contentRef.current;
    if (el && el.scrollHeight - el.clientHeight < SCROLL_END_THRESHOLD_PX) {
      setHasReachedEnd(true);
    }
  }, []);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < SCROLL_END_THRESHOLD_PX) {
      setHasReachedEnd(true);
    }
  };

  const handleAccept = () => {
    onAccept();
    trackEvent('affiliate_policy_accepted', affiliateId);
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      // No onClose handler: clicking the backdrop does nothing, so the gate can't be dismissed.
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden'
        }
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${caloTheme.palette.primary50} 0%, ${caloTheme.palette.white} 100%)`,
          borderBottom: `1px solid ${caloTheme.palette.neutral100}`,
          px: 3,
          pt: 3,
          pb: 2.5
        }}
      >
        <Icon name="calo" width={96} height={60} />
        <Box dir="rtl" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: '10px',
              backgroundColor: caloTheme.palette.primary100,
              flexShrink: 0
            }}
          >
            <PolicyOutlinedIcon sx={{ color: caloTheme.palette.primary600, fontSize: 20 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: caloTheme.palette.neutral900, textAlign: 'right' }}>
            {POLICY_TITLE}
          </Typography>
        </Box>
      </Box>

      <DialogContent
        ref={contentRef}
        onScroll={handleScroll}
        dir="rtl"
        sx={{
          maxHeight: 360,
          px: 3,
          py: 2.5,
          backgroundColor: caloTheme.palette.neutral50
        }}
      >
        <Box
          sx={{
            backgroundColor: caloTheme.palette.white,
            border: `1px solid ${caloTheme.palette.neutral100}`,
            borderRadius: 3,
            p: 2.5
          }}
        >
          <Typography
            variant="body2"
            component="div"
            sx={{
              whiteSpace: 'pre-line',
              color: caloTheme.palette.neutral800,
              lineHeight: 1.9,
              textAlign: 'right',
              fontFamily: '"Segoe UI", Tahoma, Arial, sans-serif'
            }}
          >
            {POLICY_CONTENT}
          </Typography>
        </Box>
      </DialogContent>

      <Box dir="rtl" sx={{ px: 3, pt: 2 }}>
        <FormControlLabel
          control={(
            <Checkbox
              checked={acknowledged}
              disabled={!hasReachedEnd}
              onChange={(e) => setAcknowledged(e.target.checked)}
              sx={{
                color: caloTheme.palette.neutral400,
                '&.Mui-checked': { color: caloTheme.palette.primary500 }
              }}
            />
          )}
          label={(
            <Typography variant="body2" sx={{ color: caloTheme.palette.neutral800 }}>
              لقد قرأت هذه الشروط وأوافق عليها.
            </Typography>
          )}
        />
        {!hasReachedEnd && (
          <Typography variant="caption" sx={{ display: 'block', color: caloTheme.palette.neutral600, mr: 4.5, mt: -0.5 }}>
            الرجاء التمرير حتى نهاية النص للمتابعة.
          </Typography>
        )}
      </Box>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button
          variant="contained"
          disabled={!acknowledged}
          onClick={handleAccept}
          fullWidth
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: caloTheme.palette.white,
            borderRadius: '10px',
            py: 1.2,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' }
          }}
        >
          أوافق
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PolicyGate;
